import { useEffect, useState } from 'react';
import { Button, Collapse, Form, Modal } from 'react-bootstrap';
import { ChevronDown, ChevronUp, X } from 'react-bootstrap-icons';
import { toast } from 'sonner';
import { ENDPOINTS } from '../../helpers/constants';
import { makeRESTApiRequest } from '../../helpers/api';
import {
  EMAIL_IN_TEXT_REGEX,
  blobToBase64,
  extractEmailsFromText,
  getBlobFromBlobURL,
  normalizeRichTextValue,
  textContainsEmail,
  validateEmail
} from '../../helpers/global';
import { toDisplayableRichTextHtml } from '../../helpers/safeHtml';
import AdvancedSelect from './AdvancedSelect';
import RichTextEditor from './RichTextEditor';

function ClickableEmail({ email, onClick }) {
  return (
    <button
      type="button"
      onClick={e => {
        e.preventDefault();
        onClick(email);
      }}
      className="p-0 border-0 bg-transparent align-baseline"
      style={{
        fontSize: 'inherit',
        lineHeight: 'inherit',
        color: '#237fff',
        textDecoration: 'underline',
        verticalAlign: 'baseline',
        cursor: 'pointer'
      }}
      title={`Add ${email} to Recipients`}
    >
      {email}
    </button>
  );
}

function TextWithClickableEmails({ text, onEmailClick }) {
  if (!text) return null;

  const parts = [];
  let lastIndex = 0;
  const regex = new RegExp(EMAIL_IN_TEXT_REGEX.source, 'g');
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const email = match[0];
    parts.push(
      validateEmail(email) ? (
        <ClickableEmail key={`${email}-${match.index}`} email={email} onClick={onEmailClick} />
      ) : (
        email
      )
    );
    lastIndex = match.index + email.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}

export default function SharePdf({
  show,
  onClose,
  buyerEmail,
  reportId,
  onHistoryReportChange,
  pdfUrl,
  report,
  folderId = null,
  organization = null,
  isIndividualService = false,
  individualEmail = null,
  shareEndpoint = ENDPOINTS.SHARE_REPORT,
  showIsPreliminaryCheckbox = false,
  lockPreliminaryReportCheckbox = false,
  isGeneratedReportWatermark = false,
  onShareComplete,
  submittedByName = '',
  submittedByEmail = '',
  specialInstructions = ''
}) {
  const [emails, setEmails] = useState([]);
  const [notes, setNotes] = useState('');
  const [isPreliminaryReport, setIsPreliminaryReport] = useState(false);
  const [sharingReport, setSharingReport] = useState(false);
  const [organizationEmails, setOrganizationEmails] = useState([]);
  const [selectedEmailOptions, setSelectedEmailOptions] = useState([]);
  const [availableAccurateEmails, setAvailableAccurateEmails] = useState([]);
  const [selectedAccurateEmailOptions, setSelectedAccurateEmailOptions] = useState([]);
  const [specialInstructionsExpanded, setSpecialInstructionsExpanded] = useState(false);

  const hasSubmittedBy = Boolean(submittedByName || submittedByEmail);
  const hasSpecialInstructions = Boolean(String(specialInstructions || '').trim());
  const specialInstructionsHasEmail = textContainsEmail(specialInstructions);
  useEffect(() => {
    if (!show) return;

    setIsPreliminaryReport(lockPreliminaryReportCheckbox);
    setSpecialInstructionsExpanded(specialInstructionsHasEmail);
    setNotes('');

    const seededEmails = [];
    const seen = new Set();
    const pushUnique = email => {
      const normalized = String(email || '').trim();
      if (!validateEmail(normalized)) return;
      const key = normalized.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      seededEmails.push(normalized);
    };

    pushUnique(buyerEmail);
    extractEmailsFromText(specialInstructions).forEach(pushUnique);

    setEmails(seededEmails);
    setSelectedEmailOptions(seededEmails.map(email => ({ label: email, value: email })));
  }, [show, lockPreliminaryReportCheckbox]);

  const handleEmailSelectionChange = selectedOptions => {
    setSelectedEmailOptions(selectedOptions);

    const emailValues = selectedOptions.map(option => option?.value || option);
    setEmails(emailValues);
  };

  const handleAddEmail = inputEmail => {
    const normalized = String(inputEmail || '').trim();
    if (validateEmail(normalized) && !emails.some(e => e.toLowerCase() === normalized.toLowerCase())) {
      setEmails([...emails, normalized]);
      setSelectedEmailOptions([...selectedEmailOptions, { label: normalized, value: normalized }]);
    } else {
      toast.error('Invalid email address or email already added');
    }
  };

  const onShareClick = async () => {
    if (!emails?.length) {
      return toast.error('Please insert at least one email');
    }

    const sharingToastId = toast.loading('Report sharing in progress...');

    try {
      setSharingReport(true);
      onClose();

      const uploadedFileId = await savePdf();

      if (!uploadedFileId) {
        toast.dismiss(sharingToastId);
        toast.error('Failed to save PDF');
        setSharingReport(false);
        return;
      }

      const cc_emails =
        selectedAccurateEmailOptions
          ?.map(opt => (typeof opt === 'string' ? opt : opt?.value))
          .filter(Boolean)
          .join(',') || null;

      const { response, error } = await makeRESTApiRequest({
        endpoint: shareEndpoint,
        method: 'POST',
        requestBody: {
          recepients: emails,
          notes: toDisplayableRichTextHtml(normalizeRichTextValue(notes)),
          cc: cc_emails,
          isPreliminaryReport,
          fileId: uploadedFileId,
          reportId,
          folderId
        }
      });

      if (error) {
        toast.dismiss(sharingToastId);
        toast.error(error);
        setSharingReport(false);
        return;
      }
      onHistoryReportChange(response?.generatedFiles);
      onShareComplete?.();
      toast.dismiss(sharingToastId);
      toast.success('Report shared successfully');
      setSharingReport(false);
    } catch (err) {
      toast.dismiss(sharingToastId);
      toast.error('Failed to share report');
      setSharingReport(false);
      console.error('Sharing failed:', err);
    }
  };

  const savePdf = async () => {
    try {
      const blob = await getBlobFromBlobURL(pdfUrl);

      const base64Data = await blobToBase64(blob);

      const requestBody = {
        fileName: `report_${Date.now()}.pdf`,
        mimeType: 'application/pdf',
        data: base64Data.split(',')[1],
        ...(showIsPreliminaryCheckbox
          ? {
              // check if the generated report file is a watermarked already or not
              isReportWatermark: isGeneratedReportWatermark === true,
              // if the preliminary report is checked and the watermark is not applied, then apply the watermark
              applyWaterMark: isPreliminaryReport === true && isGeneratedReportWatermark !== true
            }
          : {})
      };

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.BASE64_FILE,
        method: 'POST',
        requestBody
      });

      if (error) {
        console.error('Error saving PDF:', error);
        return null;
      }
      return response;
    } catch (err) {
      console.error('savePdf() failed:', err);
      return null;
    }
  };

  const fetchOrganizationEmails = async () => {
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.SEARCH_ORGANIZATION_EMAILS,
      method: 'POST',
      requestBody: { filter: { organization }, query: '' }
    });
    if (error) {
      toast.error('Failed to fetch organization emails');
      return;
    }
    setOrganizationEmails(response?.emails?.filter(Boolean));
    setAvailableAccurateEmails(response?.accurateEmails?.filter(Boolean));
  };

  const handleLoadOptions = async (inputEmail = '') => {
    if (inputEmail) {
      const filteredEmails = organizationEmails.filter(email =>
        email?.toLowerCase().includes(inputEmail?.toLowerCase())
      );
      return filteredEmails.map(email => ({
        label: email,
        value: email
      }));
    }

    // Return all organization emails if no input
    return organizationEmails.map(email => ({
      label: email,
      value: email
    }));
  };

  const handleLoadAccurateCcOptions = async (inputEmail = '') => {
    if (inputEmail) {
      const filtered = availableAccurateEmails.filter(email =>
        email?.toLowerCase().includes(inputEmail?.toLowerCase())
      );
      return filtered.map(email => ({ label: email, value: email }));
    }
    return availableAccurateEmails.map(email => ({ label: email, value: email }));
  };

  const handleAccurateEmailSelectionChange = selectedOptions => {
    setSelectedAccurateEmailOptions(selectedOptions ?? []);
  };

  useEffect(() => {
    const loadEmails = async () => {
      await fetchOrganizationEmails();
      if (isIndividualService) {
        setOrganizationEmails([individualEmail]);
      }
    };
    loadEmails();
  }, [organization]);

  return (
    <Modal show={show} onHide={onClose} centered size="md" style={{ zIndex: 99999 }}>
      <Modal.Header style={{ border: 'none', paddingBottom: '0' }}>
        <Modal.Title className="font-weight-bold xlargeFont">Share Report</Modal.Title>
        <Button
          variant="link"
          onClick={onClose}
          style={{
            color: '#000',
            textDecoration: 'none',
            padding: '0',
            marginRight: '10px'
          }}
        >
          <X size={20} />
        </Button>
      </Modal.Header>
      <Modal.Body style={{ padding: '20px' }}>
        {(hasSubmittedBy || hasSpecialInstructions) && (
          <div
            className="mb-3 pb-3"
            style={{ borderBottom: '1px solid #eee', fontSize: '13px', color: '#212529', lineHeight: 1.5 }}
          >
            {hasSubmittedBy && (
              <p className="mb-1">
                <span className="font-weight-bold">Submitted by:</span> {submittedByName}
                {submittedByName && submittedByEmail ? ' / ' : ''}
                {submittedByEmail ? <ClickableEmail email={submittedByEmail} onClick={handleAddEmail} /> : null}
              </p>
            )}
            {hasSpecialInstructions && (
              <div>
                <button
                  type="button"
                  className="p-0 border-0 bg-transparent d-flex align-items-center w-100 text-left"
                  onClick={() => setSpecialInstructionsExpanded(prev => !prev)}
                  aria-expanded={specialInstructionsExpanded}
                  style={{ color: 'inherit', cursor: 'pointer' }}
                >
                  <span className="font-weight-bold">Special Instructions:</span>
                  {specialInstructionsExpanded ? (
                    <ChevronUp size={14} className="ml-1 flex-shrink-0" aria-hidden />
                  ) : (
                    <ChevronDown size={14} className="ml-1 flex-shrink-0" aria-hidden />
                  )}
                </button>
                <Collapse in={specialInstructionsExpanded}>
                  <div className="mt-1" style={{ whiteSpace: 'pre-line' }}>
                    <TextWithClickableEmails text={specialInstructions} onEmailClick={handleAddEmail} />
                  </div>
                </Collapse>
              </div>
            )}
          </div>
        )}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="font-weight-bold smallFont">Recipients</Form.Label>

            <AdvancedSelect
              items={organizationEmails.map(email => ({
                label: email,
                value: email
              }))}
              selectedItems={selectedEmailOptions}
              onChange={handleEmailSelectionChange}
              placeholder="Search or Add email"
              noOptionsMessage="No items, type to add..."
              creatableLabel="Select" // example: Select "input value"
              showSelectAll={true}
              maxToShow={3}
              className="p-0 m-0 mb-2"
              creatable={true}
              searchable={true}
              onCreateOption={handleAddEmail}
              loadOptions={handleLoadOptions}
              originalOptions={organizationEmails.map(email => ({
                label: email,
                value: email
              }))}
              isMulti={true}
              useMenuPortal={false}
            />

            <p className="text-muted small mt-1 ml-2">Select or add new emails</p>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="font-weight-bold smallFont">CC</Form.Label>

            <AdvancedSelect
              items={availableAccurateEmails.map(email => ({
                label: email,
                value: email
              }))}
              selectedItems={selectedAccurateEmailOptions}
              onChange={handleAccurateEmailSelectionChange}
              placeholder="Search cc email"
              noOptionsMessage="No items..."
              creatableLabel="Select"
              showSelectAll={true}
              maxToShow={3}
              className="p-0 m-0 mb-2"
              creatable={false}
              searchable={true}
              loadOptions={handleLoadAccurateCcOptions}
              originalOptions={availableAccurateEmails.map(email => ({
                label: email,
                value: email
              }))}
              isMulti={true}
              useMenuPortal={false}
            />

            <p className="text-muted small mt-1 ml-2">Search and select emails</p>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="font-weight-bold smallFont">Notes</Form.Label>
            <RichTextEditor
              className="estoppel-notes-rich-text"
              value={notes}
              onChange={setNotes}
              placeholder="Add notes"
              height="150px"
              enableLists
            />
          </Form.Group>

          {showIsPreliminaryCheckbox && (
            <Form.Group className="mb-3 d-flex align-items-center">
              <Form.Check
                id="preliminary-report-check"
                type="checkbox"
                checked={isPreliminaryReport}
                disabled={lockPreliminaryReportCheckbox}
                onChange={e => setIsPreliminaryReport(e.target.checked)}
              />
              <Form.Label className="mb-0 font-weight-bold smallFont">Preliminary Report</Form.Label>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer style={{ border: 'none', paddingTop: '0' }}>
        <Button
          variant="light"
          onClick={onClose}
          disabled={sharingReport}
          style={{
            fontWeight: '500',
            fontSize: '14px',
            borderRadius: '4px'
          }}
        >
          Cancel
        </Button>

        <Button
          variant="dark"
          onClick={onShareClick}
          disabled={sharingReport}
          style={{
            fontWeight: '500',
            fontSize: '14px',
            borderRadius: '4px'
          }}
        >
          Share Report
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
